import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { addRecent } from "@/hooks/usePrefs";
import { api, apiError } from "@/lib/api";
import { useAtlas } from "@/context/AtlasContext";
import { RecordPanels } from "@/components/shared/RecordPanels";
import { StatusPill } from "@/components/shared/StatusPill";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { ArrowLeft, Plus, Users, Building2, X } from "lucide-react";
import { toast } from "sonner";

export default function CampaignDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { has, ctx } = useAtlas();
  const [campaign, setCampaign] = useState(null);
  const [people, setPeople] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const canEdit = has("marketing.campaigns.edit");

  const load = useCallback(() => api.get(`/marketing/campaigns/${id}`).then((r) => { setCampaign(r.data); addRecent({ type: "campaign", id, label: r.data.name, link: `/marketing/campaigns/${id}` }); }).catch(() => {}), [id]);
  useEffect(() => {
    load();
    if (has("people.view")) api.get("/people").then((r) => setPeople(r.data)).catch(() => {});
    if (has("organizations.view")) api.get("/organizations").then((r) => setOrgs(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  const addMember = async (member_type, member_id) => {
    try { await api.post(`/marketing/campaigns/${id}/audience`, { member_type, member_id }); load(); toast.success("Added to audience"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const removeMember = async (mid) => { await api.delete(`/marketing/campaigns/${id}/audience/${mid}`); load(); };

  if (!campaign) return <div className="text-slate-400">Loading…</div>;
  const audience = campaign.audience || [];
  const inAudience = new Set(audience.map((a) => a.member_id));

  return (
    <div>
      <button onClick={() => navigate("/marketing/campaigns")} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 mb-4"><ArrowLeft className="w-4 h-4" /> Campaigns</button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 space-y-4">
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <div className="flex items-center justify-between"><h1 className="text-xl font-heading font-semibold text-slate-900">{campaign.name}</h1><StatusPill value={campaign.status} /></div>
            <p className="text-sm text-slate-500 mt-2">{campaign.objective || campaign.purpose}</p>
            <div className="text-xs text-slate-500 mt-2 space-y-0.5" data-testid="campaign-meta">{campaign.target_audience && <div>Audience: <b className="text-slate-700">{campaign.target_audience}</b></div>}<div>Owner: <b className="text-slate-700">{campaign.owner_name || "— unassigned"}</b>{campaign.entity_id && ctx?.entities && <> · Entity: <b className="text-slate-700">{ctx.entities.find((e) => e.id === campaign.entity_id)?.name}</b></>}</div><div>{campaign.start_date?.slice(0, 10)} → {campaign.end_date?.slice(0, 10) || "open"}</div></div>
            <div className="flex gap-1 flex-wrap mt-3">{(campaign.channels || []).map((ch) => <span key={ch} className="text-[10px] uppercase tracking-wide bg-slate-100 text-slate-600 px-2 py-0.5 rounded capitalize">{ch.replace(/_/g, " ")}</span>)}</div>
            <div className="grid grid-cols-3 gap-2 mt-4 pt-4 border-t border-slate-100 text-center" data-testid="campaign-kpis">
              {[["Budget", `$${(campaign.budget || 0).toLocaleString()}`], ["Spend", `$${(campaign.actual_spend || 0).toLocaleString()}`, campaign.actual_spend > campaign.budget * 1.1], ["Leads", campaign.leads || 0], ["Opps", campaign.opportunities_influenced || 0], ["CPL", campaign.cpl ? `$${campaign.cpl}` : "—"], ["Conv.", campaign.conversion != null ? `${Math.round(campaign.conversion * 100)}%` : "—"], ["Pipeline", `$${(campaign.revenue_influenced || 0).toLocaleString()}`], ["ROI", campaign.roi ? `${campaign.roi}×` : "—"], ["Impr.", (campaign.impressions || 0).toLocaleString()]].map(([l, v, warn]) => (
                <div key={l}><div className="text-base font-heading font-semibold tabular" style={{ color: warn ? "var(--signal)" : undefined }}>{v}</div><div className="text-[10px] text-slate-400 uppercase tracking-wider">{l}</div></div>
              ))}
            </div>
            {campaign.performance?.length > 0 && <div className="mt-3 pt-3 border-t border-slate-100"><p className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Monthly results by channel</p>{campaign.performance.map((p) => <div key={p.id} className="text-[11px] text-slate-600 flex justify-between"><span>{p.month} · {p.channel.replace(/_/g, " ")}</span><span className="tabular">${p.spend.toLocaleString()} · {p.leads} leads · {p.opportunities} opps</span></div>)}</div>}
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2"><Users className="w-4 h-4" /> Audience ({audience.length})</h3>
              {canEdit && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild><Button size="sm" variant="outline" data-testid="audience-add-button"><Plus className="w-4 h-4" /></Button></DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-64 max-h-80 overflow-y-auto">
                    <DropdownMenuLabel>Organizations</DropdownMenuLabel>
                    {orgs.map((o) => <DropdownMenuItem key={o.id} disabled={inAudience.has(o.id)} onClick={() => addMember("organization", o.id)}><Building2 className="w-4 h-4 mr-2" />{o.name}</DropdownMenuItem>)}
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel>People</DropdownMenuLabel>
                    {people.map((p) => <DropdownMenuItem key={p.id} disabled={inAudience.has(p.id)} onClick={() => addMember("person", p.id)}><Users className="w-4 h-4 mr-2" />{p.first_name} {p.last_name}</DropdownMenuItem>)}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
            {audience.length === 0 && <p className="text-sm text-slate-400">No audience members yet.</p>}
            <div className="space-y-1.5">
              {audience.map((a) => (
                <div key={a.id} className="flex items-center gap-2 text-sm text-slate-700" data-testid={`audience-${a.id}`}>
                  {a.member_type === "organization" ? <Building2 className="w-3.5 h-3.5 text-slate-400" /> : <Users className="w-3.5 h-3.5 text-slate-400" />}
                  <span className="flex-1">{a.label}</span>
                  {canEdit && <button onClick={() => removeMember(a.id)} className="text-slate-300 hover:text-red-500"><X className="w-3.5 h-3.5" /></button>}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="lg:col-span-2">
          <RecordPanels relatedType="campaign" relatedId={campaign.id} relatedLabel={campaign.name} sourceApp="marketing" />
        </div>
      </div>
    </div>
  );
}
